import {
  DEFAULT_AIRCRAFT_ID,
  DEFAULT_FUEL_LBS_PER_GALLON,
  type FlightLeg,
} from "./types";
import { isTexasDestinationIcao } from "./texasIcao";

export function fillLegRowFromFlightLeg(row: HTMLElement, leg: FlightLeg): void {
  row.dataset.legId = leg.id ?? "";

  const q = (field: string) =>
    row.querySelector<HTMLInputElement>(`[data-f="${field}"]`);

  const setVal = (field: string, value: string) => {
    const el = q(field);
    if (el) el.value = value;
  };

  setVal("date", leg.flightDate);
  setVal("from", leg.fromIcao);
  setVal("to", leg.toIcao);
  setVal("pax", String(leg.passengerCount));
  const domEl = q("dom_pax");
  if (domEl) {
    domEl.value =
      leg.passengersDomesticSegmentTax === null
        ? ""
        : String(leg.passengersDomesticSegmentTax);
  }
  setVal("charter", String(leg.charterOrFareAmountUsd));
  setVal("intl", String(leg.internationalFacilityPassengers));
  setVal("akhi", String(leg.alaskaHawaiiDomesticDeparturePassengers));
  setVal("fuel_lbs", leg.fuelLbs === null ? "" : String(leg.fuelLbs));
  setVal(
    "fuel_gal",
    leg.fuelGallonsReported === null ? "" : String(leg.fuelGallonsReported)
  );
  const rural = q("rural");
  if (rural) rural.checked = leg.ruralAirportExempt;
  const skip = q("skip");
  if (skip) skip.checked = leg.excludeFrom720;
  const personal = q("personal");
  if (personal) personal.checked = Boolean(leg.personalUse);
  setVal(
    "aircraft",
    leg.aircraftId && leg.aircraftId !== DEFAULT_AIRCRAFT_ID
      ? leg.aircraftId
      : DEFAULT_AIRCRAFT_ID
  );
  {
    const raw = leg.routeNotes ?? "";
    const nl = raw.indexOf("\n");
    if (nl >= 0) {
      setVal("route", raw.slice(0, nl).trim());
      setVal("notes", raw.slice(nl + 1).trim());
    } else {
      setVal("route", "");
      setVal("notes", raw);
    }
  }
  setVal(
    "tach_start",
    leg.tachStart !== null && leg.tachStart !== undefined ? String(leg.tachStart) : ""
  );
  setVal(
    "tach_end",
    leg.tachEnd !== null && leg.tachEnd !== undefined ? String(leg.tachEnd) : ""
  );
  setVal(
    "fuel_price",
    leg.fuelPricePerGallon !== null &&
      leg.fuelPricePerGallon !== undefined &&
      Number.isFinite(leg.fuelPricePerGallon)
      ? String(leg.fuelPricePerGallon)
      : ""
  );
  setVal("crew", leg.crewList ?? "");
  setVal("passengers", leg.passengerList ?? "");
  const txArrival = row.querySelector<HTMLInputElement>('[data-f="texas_arrival"]');
  if (txArrival) txArrival.checked = Boolean(leg.texasArrival);
  setVal(
    "hours",
    leg.flightHours !== null && leg.flightHours !== undefined
      ? String(leg.flightHours)
      : ""
  );
  setVal(
    "landings",
    leg.landings !== null && leg.landings !== undefined ? String(leg.landings) : "1"
  );
  setVal(
    "takeoffs",
    leg.takeoffs !== null && leg.takeoffs !== undefined ? String(leg.takeoffs) : "1"
  );
  setVal("dep_state", leg.departureState ?? "");

  const details = row.querySelector<HTMLDetailsElement>(".leg-tax-details");
  if (details) {
    const hasAdvanced =
      leg.passengersDomesticSegmentTax != null ||
      leg.internationalFacilityPassengers > 0 ||
      leg.charterOrFareAmountUsd > 0 ||
      leg.alaskaHawaiiDomesticDeparturePassengers > 0 ||
      Boolean(leg.departureState?.trim()) ||
      leg.texasArrival ||
      leg.ruralAirportExempt ||
      leg.excludeFrom720;
    details.open = hasAdvanced;
  }
}

/** Replace all cards with the given legs (at least one empty row if legs empty). */
export function hydrateFormWithLegs(
  container: HTMLElement,
  legs: FlightLeg[],
  lbsPerGallon?: number
): void {
  container.innerHTML = "";
  if (legs.length === 0) {
    container.innerHTML = formLegRowHtml();
    if (lbsPerGallon != null && lbsPerGallon > 0) {
      const row = container.querySelector<HTMLElement>("[data-leg-row]");
      if (row) initLegRowChrome(row, lbsPerGallon);
    }
    return;
  }
  for (const leg of legs) {
    container.insertAdjacentHTML("beforeend", formLegRowHtml());
    const all = container.querySelectorAll<HTMLElement>("[data-leg-row]");
    const row = all[all.length - 1];
    if (row) fillLegRowFromFlightLeg(row, leg);
  }
  if (lbsPerGallon != null && lbsPerGallon > 0) {
    refreshAllLegRowChrome(container, lbsPerGallon);
  }
}

/** Gallons = lbs ÷ lbsPerGallon (e.g. Jet A ~6.5). Clears gal when lbs cleared. */
export function updateFuelGallonsForRow(
  row: HTMLElement,
  lbsPerGallon: number
): void {
  const lbsEl = row.querySelector<HTMLInputElement>('[data-f="fuel_lbs"]');
  const galEl = row.querySelector<HTMLInputElement>('[data-f="fuel_gal"]');
  if (!lbsEl || !galEl) return;

  const raw = lbsEl.value.trim();
  if (raw === "") {
    galEl.value = "";
    return;
  }

  const lbs = Number(raw);
  if (!Number.isFinite(lbs) || lbs < 0) return;
  if (!Number.isFinite(lbsPerGallon) || lbsPerGallon <= 0) return;

  const gal = lbs / lbsPerGallon;
  galEl.value = String(Math.round(gal * 100) / 100);
}

export function syncAllFuelGallonsFromLbs(
  root: HTMLElement,
  lbsPerGallon: number
): void {
  for (const row of root.querySelectorAll<HTMLElement>("[data-leg-row]")) {
    const lbsEl = row.querySelector<HTMLInputElement>('[data-f="fuel_lbs"]');
    if (lbsEl?.value?.trim()) {
      updateFuelGallonsForRow(row, lbsPerGallon);
    }
    updateFuelGalVisibility(row);
  }
}

/** Gallons row is always visible; values follow lbs (or legacy gal-only rows). */
export function updateFuelGalVisibility(row: HTMLElement): void {
  const wrap = row.querySelector(".leg-fuel-gal-wrap");
  wrap?.classList.remove("leg-fuel-gal-hidden");
}

export function maybeAutoTexasArrival(row: HTMLElement): void {
  const toEl = row.querySelector<HTMLInputElement>('[data-f="to"]');
  const tx = row.querySelector<HTMLInputElement>('[data-f="texas_arrival"]');
  if (!toEl || !tx) return;
  const code = toEl.value.trim().toUpperCase();
  if (code.length < 4) return;
  tx.checked = isTexasDestinationIcao(code);
}

export function initLegRowChrome(row: HTMLElement, lbsPerGallon: number): void {
  updateFuelGalVisibility(row);
  const lbsEl = row.querySelector<HTMLInputElement>('[data-f="fuel_lbs"]');
  if (lbsEl?.value?.trim()) {
    updateFuelGallonsForRow(row, lbsPerGallon);
  }
}

export function refreshAllLegRowChrome(root: HTMLElement, lbsPerGallon: number): void {
  for (const row of root.querySelectorAll<HTMLElement>("[data-leg-row]")) {
    initLegRowChrome(row, lbsPerGallon);
  }
}

function numOr(
  el: HTMLInputElement | null,
  emptyAs: number | null
): number | null {
  if (!el) return emptyAs;
  const t = el.value.trim();
  if (t === "") return emptyAs;
  const n = Number(t);
  if (!Number.isFinite(n)) throw new Error(`Invalid number: ${t}`);
  return n;
}

/** Read manual entry cards under `root` (e.g. #form-legs). Skips completely empty cards. */
export function readLegsFromManualForm(
  root: HTMLElement,
  fuelLbsPerGallon: number = DEFAULT_FUEL_LBS_PER_GALLON
): FlightLeg[] {
  const cards = root.querySelectorAll<HTMLElement>("[data-leg-row]");
  const legs: FlightLeg[] = [];
  let idx = 0;

  for (const row of cards) {
    idx += 1;
    const q = (sel: string) => row.querySelector<HTMLInputElement>(sel);

    const date = q('[data-f="date"]')?.value?.trim() ?? "";
    const from = q('[data-f="from"]')?.value?.trim().toUpperCase() ?? "";
    const to = q('[data-f="to"]')?.value?.trim().toUpperCase() ?? "";
    const paxS = q('[data-f="pax"]')?.value?.trim() ?? "";

    if (!date && !from && !to && !paxS) continue;

    if (!date) throw new Error(`Flight ${idx}: date is required`);
    if (!from || !to) throw new Error(`Flight ${idx}: from and to are required`);
    if (!paxS) throw new Error(`Flight ${idx}: passenger count is required`);

    const pax = parseInt(paxS, 10);
    if (Number.isNaN(pax)) throw new Error(`Flight ${idx}: invalid passenger count`);

    const segments = 1;

    const domPaxEl = q('[data-f="dom_pax"]');
    const domPaxRaw = domPaxEl?.value?.trim() ?? "";
    const passengersDomesticSegmentTax: number | null =
      domPaxRaw === "" ? null : parseInt(domPaxRaw, 10);
    if (domPaxRaw !== "" && Number.isNaN(passengersDomesticSegmentTax!)) {
      throw new Error(`Flight ${idx}: invalid dom pax`);
    }

    const charter = numOr(q('[data-f="charter"]'), 0) ?? 0;
    const intl =
      parseInt(q('[data-f="intl"]')?.value?.trim() || "0", 10) || 0;
    const akhi =
      parseInt(q('[data-f="akhi"]')?.value?.trim() || "0", 10) || 0;

    const fuelLbsRaw = q('[data-f="fuel_lbs"]')?.value?.trim() ?? "";
    const fuelGalRaw = q('[data-f="fuel_gal"]')?.value?.trim() ?? "";
    let fuelLbs: number | null = null;
    let fuelGal: number | null = null;
    if (fuelLbsRaw !== "") {
      const lbs = Number(fuelLbsRaw);
      if (!Number.isFinite(lbs) || lbs < 0) {
        throw new Error(`Flight ${idx}: invalid fuel lbs`);
      }
      fuelLbs = lbs;
      if (Number.isFinite(fuelLbsPerGallon) && fuelLbsPerGallon > 0) {
        fuelGal = Math.round((lbs / fuelLbsPerGallon) * 100) / 100;
      }
    } else if (fuelGalRaw !== "") {
      const gal = Number(fuelGalRaw);
      if (!Number.isFinite(gal) || gal < 0) {
        throw new Error(`Flight ${idx}: invalid fuel gallons`);
      }
      if (!Number.isFinite(fuelLbsPerGallon) || fuelLbsPerGallon <= 0) {
        throw new Error(
          `Flight ${idx}: enter fuel lbs, or set the worksheet Jet-A lbs/gal rate above`
        );
      }
      fuelGal = gal;
      fuelLbs = Math.round(gal * fuelLbsPerGallon * 100) / 100;
    } else {
      throw new Error(
        `Flight ${idx}: fuel lbs is required (gallons auto-calculate; imported gal-only rows are OK until you save)`
      );
    }

    const rural = q('[data-f="rural"]')?.checked ?? false;
    const personal = q('[data-f="personal"]')?.checked ?? false;
    const skip = q('[data-f="skip"]')?.checked ?? false;

    const hoursRaw = q('[data-f="hours"]')?.value?.trim() ?? "";
    let flightHours: number | null = null;
    if (hoursRaw !== "") {
      const hh = Number(hoursRaw);
      if (!Number.isFinite(hh) || hh < 0) {
        throw new Error(`Flight ${idx}: invalid flight hours`);
      }
      flightHours = hh;
    }

    const landRaw = q('[data-f="landings"]')?.value?.trim() ?? "";
    let landings: number;
    if (landRaw === "") {
      landings = 1;
    } else {
      const ld = parseInt(landRaw, 10);
      if (Number.isNaN(ld) || ld < 0) {
        throw new Error(`Flight ${idx}: invalid landings`);
      }
      landings = ld;
    }

    const takeRaw = q('[data-f="takeoffs"]')?.value?.trim() ?? "";
    let takeoffs: number;
    if (takeRaw === "") {
      takeoffs = 1;
    } else {
      const tk = parseInt(takeRaw, 10);
      if (Number.isNaN(tk) || tk < 0) {
        throw new Error(`Flight ${idx}: invalid takeoffs`);
      }
      takeoffs = tk;
    }

    const depStateRaw = q('[data-f="dep_state"]')?.value?.trim().toUpperCase() ?? "";
    const departureState = depStateRaw.length === 2 ? depStateRaw : null;

    const idRaw = (row as HTMLElement).dataset.legId?.trim();
    const dbId = idRaw ? idRaw : undefined;

    const routeVal = q('[data-f="route"]')?.value?.trim() ?? "";
    const notesVal = q('[data-f="notes"]')?.value?.trim() ?? "";
    const routeNotes =
      routeVal && notesVal
        ? `${routeVal}\n${notesVal}`
        : routeVal || notesVal;

    const tachStartRaw = q('[data-f="tach_start"]')?.value?.trim() ?? "";
    const tachEndRaw = q('[data-f="tach_end"]')?.value?.trim() ?? "";
    let tachStart: number | null = null;
    let tachEnd: number | null = null;
    if (tachStartRaw !== "") {
      const ts = Number(tachStartRaw);
      if (!Number.isFinite(ts)) throw new Error(`Flight ${idx}: invalid tach start`);
      tachStart = ts;
    }
    if (tachEndRaw !== "") {
      const te = Number(tachEndRaw);
      if (!Number.isFinite(te)) throw new Error(`Flight ${idx}: invalid tach end`);
      tachEnd = te;
    }

    const fuelPriceRaw = q('[data-f="fuel_price"]')?.value?.trim() ?? "";
    let fuelPricePerGallon: number | null = null;
    if (fuelPriceRaw !== "") {
      const fp = Number(fuelPriceRaw);
      if (!Number.isFinite(fp) || fp < 0) {
        throw new Error(`Flight ${idx}: invalid fuel price per gallon`);
      }
      fuelPricePerGallon = fp;
    }

    const crewListRaw = q('[data-f="crew"]')?.value?.trim() ?? "";
    const crewList = crewListRaw === "" ? null : crewListRaw;

    const passengerListRaw = q('[data-f="passengers"]')?.value?.trim() ?? "";
    const passengerList = passengerListRaw === "" ? null : passengerListRaw;

    const texasArrival = q('[data-f="texas_arrival"]')?.checked ?? false;

    const aircraftRaw = q('[data-f="aircraft"]')?.value?.trim() || DEFAULT_AIRCRAFT_ID;

    legs.push({
      ...(dbId ? { id: dbId } : {}),
      flightDate: date,
      fromIcao: from,
      toIcao: to,
      passengerCount: pax,
      segmentsDomestic: segments,
      passengersDomesticSegmentTax,
      internationalFacilityPassengers: intl,
      alaskaHawaiiDomesticDeparturePassengers: akhi,
      charterOrFareAmountUsd: charter,
      ruralAirportExempt: rural,
      personalUse: personal,
      excludeFrom720: skip,
      fuelLbs,
      fuelGallonsReported: fuelGal,
      aircraftId: aircraftRaw.toUpperCase().slice(0, 12),
      routeNotes,
      tachStart,
      tachEnd,
      fuelPricePerGallon,
      passengerList,
      crewList,
      texasArrival,
      flightHours,
      landings,
      takeoffs,
      departureState,
    });
  }

  return legs;
}

export function formLegRowHtml(): string {
  return `
    <div class="leg-card" data-leg-row>
      <div class="leg-card-head">
        <span class="leg-card-title">Flight</span>
        <button type="button" class="leg-remove" data-remove aria-label="Remove this leg">Remove</button>
      </div>
      <div class="leg-grid leg-grid-main">
        <label class="field">Date
          <input data-f="date" type="date" />
        </label>
        <label class="field field-airport-ac">From airport
          <input data-f="from" type="text" maxlength="8" placeholder="KSLC" autocomplete="off" />
        </label>
        <label class="field field-airport-ac">To airport
          <input data-f="to" type="text" maxlength="8" placeholder="KHOU" autocomplete="off" />
        </label>
        <label class="field">Route
          <input data-f="route" type="text" placeholder="optional" autocomplete="off" />
        </label>
        <label class="field">Pax
          <input data-f="pax" type="number" min="0" step="1" placeholder="0" />
        </label>
        <label class="field field-passengers-ac leg-span-crew">Crew
          <span class="field-hint">Optional; not counted as fare passengers. With cloud save, matches your <strong>Data</strong> contacts as you type. Use <code>;</code> between names.</span>
          <input data-f="crew" type="text" placeholder="crew (optional)" name="crew" autocomplete="off" />
        </label>
        <label class="field field-passengers-ac leg-span-passengers">Passengers (guests / non-crew)
          <span class="field-hint">If crew is set, only these names drive domestic-segment excise. With Supabase, roster matches appear as you type; otherwise type full names. <code>;</code> between names.</span>
          <input data-f="passengers" type="text" placeholder="guests (optional)" name="passengers" autocomplete="off" />
        </label>
      </div>
      <div class="leg-grid leg-grid-flight">
        <label class="field">Fuel lbs
          <input data-f="fuel_lbs" type="number" min="0" step="1" placeholder="required" required />
        </label>
        <label class="field leg-fuel-gal-wrap">Gallons
          <span class="field-hint">from lbs ÷ rate</span>
          <input data-f="fuel_gal" type="number" min="0" step="0.1" readonly tabindex="-1" aria-readonly="true" />
        </label>
        <label class="field">Fuel price
          <input data-f="fuel_price" type="number" min="0" step="0.01" placeholder="$/gal" />
        </label>
        <label class="field">Tach start
          <input data-f="tach_start" type="number" min="0" step="0.1" placeholder="optional" />
        </label>
        <label class="field">Tach end
          <input data-f="tach_end" type="number" min="0" step="0.1" placeholder="optional" />
        </label>
        <label class="field">Hours
          <input data-f="hours" type="number" min="0" step="0.1" placeholder="optional" />
        </label>
        <label class="field">Landings
          <input data-f="landings" type="number" min="0" step="1" value="1" />
        </label>
        <label class="field">Takeoffs
          <input data-f="takeoffs" type="number" min="0" step="1" value="1" />
        </label>
      </div>
      <details class="leg-tax-details">
        <summary class="leg-tax-summary">Tax details</summary>
        <div class="leg-grid leg-grid-tax">
          <label class="field">Domestic pax
            <input data-f="dom_pax" type="number" min="0" step="1" placeholder="blank = Pax" />
          </label>
          <label class="field">Intl pax
            <input data-f="intl" type="number" min="0" step="1" placeholder="0" />
          </label>
          <label class="field">Taxable fare
            <input data-f="charter" type="number" min="0" step="0.01" placeholder="0" />
          </label>
          <label class="field">AK/HI dep pax
            <input data-f="akhi" type="number" min="0" step="1" placeholder="0" title="Passengers for IRS No. 26 Alaska/Hawaii domestic departure. Leave 0 to infer from ICAO: departure PA… (Alaska) or PH… (Hawaii) with US domestic arrival uses the same count as domestic segment pax." />
          </label>
          <label class="field">Depart state
            <input data-f="dep_state" type="text" maxlength="2" placeholder="TX" title="Two-letter departure state" autocomplete="off" />
          </label>
          <label class="field leg-check leg-check-tax">
            <input data-f="texas_arrival" type="checkbox" title="Landing in Texas; combined with landings for program metrics" />
            <span>TX landing</span>
          </label>
          <label class="field leg-check leg-check-tax">
            <input data-f="rural" type="checkbox" title="Rural airport exemption for domestic segment tax" />
            <span>Rural exempt</span>
          </label>
          <label class="field leg-check leg-check-tax">
            <input data-f="skip" type="checkbox" title="Exclude this leg from Form 720 passenger air taxes (e.g. fuel-only)" />
            <span>Exclude from 720</span>
          </label>
        </div>
      </details>
      <div class="leg-grid leg-grid-tail">
        <label class="field">Aircraft
          <input data-f="aircraft" type="text" value="${DEFAULT_AIRCRAFT_ID}" autocomplete="off" />
        </label>
        <label class="field leg-check leg-check-inline">
          <input data-f="personal" type="checkbox" title="Non-business; passenger reimburses air excise to Utah Gas Corp" />
          <span>Personal / reimburse UGC</span>
        </label>
        <label class="field leg-span-notes">Purpose / notes
          <input data-f="notes" type="text" placeholder="optional" autocomplete="off" />
        </label>
      </div>
    </div>
  `;
}
